import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { InventoryItem } from '../types';

interface InventoryContextType {
  inventory: InventoryItem[];
  loading: boolean;
  addItem: (item: Omit<InventoryItem, 'id'>) => Promise<void>;
  updateItem: (item: InventoryItem) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  getItemById: (id: string) => InventoryItem | undefined;
  updateStock: (id: string, quantity: number) => Promise<void>;
  fetchInventory: () => Promise<void>;
}

const InventoryContext = createContext<InventoryContextType | undefined>(undefined);

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
};

export const InventoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch inventory from Supabase when authenticated and organization is selected
  useEffect(() => {
    if (isAuthenticated && currentOrganization) {
      fetchInventory();
    }
  }, [isAuthenticated, currentOrganization]);

  const fetchInventory = async () => {
    if (!currentOrganization) {
      console.warn('⚠️ No organization selected. Cannot fetch inventory.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('📦 Fetching inventory for organization:', currentOrganization.id);
      
      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('name', { ascending: true });

      if (error) {
        console.error('❌ Error fetching inventory:', error);
        return;
      }

      if (data) {
        // Transform Supabase data to InventoryItem format
        const transformedInventory: InventoryItem[] = data.map(item => ({
          id: item.id,
          name: item.name,
          description: item.description || '',
          category: item.category,
          stock: parseFloat(item.stock) || 0,
          unitPrice: parseFloat(item.unit_price) || 0,
          minimumStock: item.minimum_stock ? parseFloat(item.minimum_stock) : undefined,
          unit: item.unit || 'pcs'
        }));

        console.log(`✅ Loaded ${transformedInventory.length} inventory items`);
        setInventory(transformedInventory);
      }
    } catch (error) {
      console.error('❌ Error in fetchInventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const addItem = async (item: Omit<InventoryItem, 'id'>) => {
    if (!currentOrganization) {
      throw new Error('No organization selected. Please select an organization first.');
    }

    try {
      console.log('➕ Adding inventory item for organization:', currentOrganization.id);
      
      const { data, error } = await supabase
        .from('inventory_items')
        .insert({
          organization_id: currentOrganization.id,
          name: item.name,
          description: item.description,
          category: item.category,
          stock: item.stock,
          unit_price: item.unitPrice,
          minimum_stock: item.minimumStock,
          unit: item.unit
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error adding inventory item:', error);
        throw error;
      }

      if (data) {
        const newItem: InventoryItem = {
          id: data.id,
          name: data.name,
          description: data.description || '',
          category: data.category,
          stock: parseFloat(data.stock) || 0,
          unitPrice: parseFloat(data.unit_price) || 0,
          minimumStock: data.minimum_stock ? parseFloat(data.minimum_stock) : undefined,
          unit: data.unit || 'pcs'
        };
        console.log('✅ Inventory item added successfully:', newItem.name);
        setInventory(prev => [...prev, newItem]);
      }
    } catch (error) {
      console.error('❌ Error in addItem:', error);
      throw error;
    }
  };

  const updateItem = async (updatedItem: InventoryItem) => {
    try {
      const { error } = await supabase
        .from('inventory_items')
        .update({
          name: updatedItem.name,
          description: updatedItem.description,
          category: updatedItem.category,
          stock: updatedItem.stock,
          unit_price: updatedItem.unitPrice,
          minimum_stock: updatedItem.minimumStock,
          unit: updatedItem.unit
        })
        .eq('id', updatedItem.id);

      if (error) {
        console.error('Error updating inventory item:', error);
        throw error;
      }

      setInventory(prev => 
        prev.map(item => 
          item.id === updatedItem.id ? updatedItem : item
        )
      );
    } catch (error) {
      console.error('Error in updateItem:', error);
      throw error;
    }
  };

  const deleteItem = async (id: string) => {
    try {
      const { error } = await supabase
        .from('inventory_items')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting inventory item:', error);
        throw error;
      }

      setInventory(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error in deleteItem:', error);
      throw error;
    }
  };

  const getItemById = (id: string) => {
    return inventory.find(item => item.id === id);
  };

  const updateStock = async (id: string, quantity: number) => {
    try {
      // Get current stock first
      const currentItem = inventory.find(item => item.id === id);
      if (!currentItem) {
        throw new Error('Inventory item not found');
      }

      const newStock = currentItem.stock + quantity;
      
      // Prevent negative stock
      if (newStock < 0) {
        throw new Error('Cannot reduce stock below zero');
      }

      const { error } = await supabase
        .from('inventory_items')
        .update({ stock: newStock })
        .eq('id', id);

      if (error) {
        console.error('Error updating stock:', error);
        throw error;
      }

      setInventory(prev => 
        prev.map(item => {
          if (item.id === id) {
            return {
              ...item,
              stock: newStock
            };
          }
          return item;
        })
      );
    } catch (error) {
      console.error('Error in updateStock:', error);
      throw error;
    }
  };

  return (
    <InventoryContext.Provider value={{
      inventory,
      loading,
      addItem,
      updateItem,
      deleteItem,
      getItemById,
      updateStock,
      fetchInventory
    }}>
      {children}
    </InventoryContext.Provider>
  );
};